// Competitor facts sourced via live web research 2026-08-27 (see roadmap for sources).
// Keep these honest and current — re-verify before making claims stale beyond a few months.
// AureliusCo's own numbers are pulled live elsewhere (api.aureliusco.in/api/plans) except here
// where used for direct comparison text, matching the same real figures (Basic $17, Growth $41, Elite $66).

export interface Competitor {
  slug: string;
  name: string;
  tagline: string;
  pricingSummary: string;
  pricingModel: string;
  channels: string;
  strengths: string[];
  tradeoffs: string[];
  bestFor: string;
  aureliusAdvantage: string;
}

export const COMPETITORS: Competitor[] = [
  {
    slug: "manychat",
    name: "ManyChat",
    tagline: "The industry-leading multi-channel chat automation platform",
    pricingSummary: "Free (25 contacts, 2 channels), Essential $14/mo (250 contacts), Pro $29/mo (2,500 contacts), Business $69/mo (7,500 contacts). AI features are a separate ~$29/mo add-on. WhatsApp messages billed separately by Meta.",
    pricingModel: "Priced per active contact (subscriber count), with overage fees once you exceed your plan's contact limit — a viral post that brings in a lot of new contacts can push you into a higher tier mid-month.",
    channels: "Instagram, Facebook Messenger, WhatsApp, TikTok",
    strengths: [
      "Most mature product on the market, with a visual drag-and-drop automation builder",
      "True multi-channel support — one automation can span Instagram, WhatsApp, and Messenger",
      "Large ecosystem: Zapier/Make integrations, extensive template library, big community",
    ],
    tradeoffs: [
      "Pricing scales with total contact count, not usage — costs can climb fast as your audience grows",
      "AI-powered replies are a separate paid add-on on top of your plan",
      "Broader multi-channel scope means more surface area to configure than a focused Instagram tool",
    ],
    bestFor: "Businesses that need automation across Instagram, WhatsApp, and Messenger from one platform, and don't mind contact-based pricing.",
    aureliusAdvantage: "AureliusCo prices by connected accounts and daily reply volume, not total contact count — your cost doesn't spike because a post goes viral. It's also purpose-built for Instagram only, so there's no multi-channel setup overhead if Instagram is your only channel.",
  },
  {
    slug: "linkdm",
    name: "LinkDM",
    tagline: "Instagram DM automation priced per message sent",
    pricingSummary: "Free (1,000 DMs/mo, 1 account), Pro $19/mo (25,000 DMs, 3 accounts), Platinum+ $99/mo (300,000 DMs, 10 accounts), custom Enterprise above that.",
    pricingModel: "Priced per DM sent per month, not per contact — a viral post eats into your monthly DM allowance rather than multiplying your bill outright.",
    channels: "Instagram (comments, DMs, Stories, Facebook comments)",
    strengths: [
      "Official Meta Tech Partner, built on Instagram's official API",
      "DM-volume pricing model avoids per-contact cost spikes",
      "Broad trigger coverage: posts, Stories, comments, IG Live (on paid plans)",
    ],
    tradeoffs: [
      "Entry-tier free plan caps out at a relatively low 1,000 DMs/month",
      "Lead-capture integrations (Kit, Flodesk) are limited to specific paid tiers",
      "No distinction between a scripted reply and an AI-generated one — replies are template/flow-based",
    ],
    bestFor: "Creators and businesses who want simple, DM-volume-priced Instagram automation without a contact-based pricing model.",
    aureliusAdvantage: "AureliusCo gives you a choice per automation between a Fixed Message and a Smart AI reply that generates context-aware responses from a prompt — not just template flows. Plans are priced by account + daily reply cap rather than total DM volume.",
  },
  {
    slug: "zorcha",
    name: "Zorcha",
    tagline: "India-built Instagram automation with a free tier",
    pricingSummary: "Free forever tier for core DM automation. Paid: Pro from ₹1,199/mo, Ultimate from ₹2,999/mo, Business from ₹8,999/mo (India/INR pricing).",
    pricingModel: "INR-denominated tiered plans, with a genuinely free entry tier for core comment/DM automation.",
    channels: "Instagram (comments, DMs, mentions)",
    strengths: [
      "Free tier covers real automation, not just a trial",
      "India-focused pricing and support, Meta Verified Tech Provider",
      "AI-driven conversation handling across comments, DMs, and mentions",
    ],
    tradeoffs: [
      "Primarily India-market focused — pricing and positioning may be less suited for international/USD billing",
      "Less established outside the Indian creator/business market compared to global players like ManyChat",
    ],
    bestFor: "India-based creators and businesses wanting free-to-start Instagram automation with local pricing and support.",
    aureliusAdvantage: "AureliusCo supports both INR and USD billing depending on your region, and every plan — not just a free tier — includes a 14-day trial with full automation limits, so you can evaluate the exact plan you'd actually use before paying.",
  },
  {
    slug: "superprofile",
    name: "SuperProfile",
    tagline: "All-in-one creator platform with AutoDM built in",
    pricingSummary: "Free Starter plan with unlimited AutoDM for post replies. Creator plan ₹99 first month then ₹499/mo (unlimited AutoDM across posts/stories/lives, multi-account). Pro plan is custom-priced.",
    pricingModel: "Bundled pricing — AutoDM is one feature inside a broader plan that also includes a link-in-bio page, digital storefront, and community tools.",
    channels: "Instagram (posts, Stories, Lives) — bundled with link-in-bio and storefront",
    strengths: [
      "Free tier includes unlimited AutoDM for post replies",
      "Bundles link-in-bio, storefront, and community alongside automation — one platform for creators selling products",
      "Official Meta Tech Partner",
    ],
    tradeoffs: [
      "AutoDM is one part of a larger creator-monetization platform, not a dedicated automation product — you're paying for features you may not use if automation is all you need",
      "Comparing pricing directly is harder since the plan bundles several unrelated tools together",
    ],
    bestFor: "Creators who want a link-in-bio page, storefront, and Instagram automation bundled into one subscription.",
    aureliusAdvantage: "AureliusCo is purpose-built for Instagram automation only — no bundled storefront or link-in-bio you're paying for but not using. If you already have those tools elsewhere, you're not paying twice.",
  },
];

export function getCompetitor(slug: string): Competitor | undefined {
  return COMPETITORS.find((c) => c.slug === slug);
}
